// FunderContributions.tsx
import { useAuth } from "@/auth/hooks/useAuth";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { convertEuroToCurrency } from "@/libs/amount";
import { ChevronDown, ChevronRight, ExternalLink, Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ContributionTrackingIntroPanel, {
  ContributionCalendarDetailItem,
  ContributionCalendarEvent,
  ContributionReminderItem,
  ContributionTrackingStat,
} from "./ContributionTrackingIntroPanel";
import FieldInput from "./FieldInput";
import NewContributionsSkeleton from "./NewContributionsSkeleton";
import ProgramProjectFilter from "./ProgramProjectFilter";
import VersementCard from "./VersementCard";

/** ===================== Types ===================== */
type PaidFile = { id: number; name?: string; uri?: string };

type RealFundingPaid = {
  id?: number;
  realfunding: number;
  amount?: number; // EUR (base)
  amountConverted?: number; // Montant déjà converti (devise du projet) côté backend
  oldRate?: number | null; // Taux utilisé pour amountConverted
  dateOldRate?: string | Date | null; // Date du taux utilisé
  at: string | Date | null;
  pjs?: PaidFile[];
};

type RealFundingExpectedPaid = {
  id?: number | string;
  realfunding: number;
  amount?: number; // EUR base
  at: string | Date | null;
  status?: number;
  datePaid?: string | Date | null;
  amountConverted?: number | null;
  oldRate?: number | null;
  dateOldRate?: string | Date | null;
};

type Currency = { id: number; code: string; symbol?: string };

type Country = { currency?: Currency };

type Project = {
  id: number;
  name: string;
  country?: Country;
  realFundings: Array<{
    id: number;
    financier?: { id: number; fullname: string };
    representant?: { id: number; firstname: string; lastname: string };
    verdeo?: boolean;
    paidAmount?: number; // EUR base
    paidPjs?: PaidFile[];
    dateAmount?: string | Date | null;
    realFundingPaids?: RealFundingPaid[];
    realFundingExpectedPaids?: RealFundingExpectedPaid[];

    // éventuelles structures côté Verdeo (toujours ignorées pour VersementCard ici)
    verdeoFundingInvoiced?: any;
    verdeoFundingExpectedPaids?: Array<
      RealFundingExpectedPaid & {
        status?: number;
        amountConverted?: number;
        oldRate?: number;
        dateOldRate?: string | Date | null;
      }
    >;
  }>;
};

type APIResp = {
  data?: any;
};

type ContributionReminderSection = "today" | "upcoming" | "overdue";

function readFundingIsVerdeo(funding: any): boolean {
  const raw = funding?.verdeo ?? funding?.viaVerdeo ?? funding?.isVerdeo;
  return raw === true || raw === 1 || raw === "1";
}

function toValidDate(value: any): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDayStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toDayKey(date: Date) {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isExpectedPaidAlreadyReceived(paid: any) {
  if (Number(paid?.status) === 2) return true;
  if (paid?.datePaid) return true;
  return false;
}

function buildContributionTrackingModel(projects: Project[]) {
  const today = toDayStart(new Date());
  const upcomingLimit = new Date(today);
  upcomingLimit.setDate(upcomingLimit.getDate() + 7);

  let contractsCount = 0;
  let contractsVerdeoCount = 0;
  let contractsInternalCount = 0;
  let plannedOutstandingCount = 0;
  let plannedOutstandingAmount = 0;
  let overdueCount = 0;
  let overdueAmount = 0;
  let todayCount = 0;
  let todayAmount = 0;
  let upcomingCount = 0;
  let upcomingAmount = 0;
  let receivedCount = 0;
  let receivedAmount = 0;

  const calendarEvents: ContributionCalendarEvent[] = [];
  const reminderMap = new Map<string, ContributionReminderItem>();

  const pushReminder = (
    section: ContributionReminderSection,
    label: string,
    date: Date,
    targetId?: string,
    projectId?: number,
    amount: number = 0,
  ) => {
    const day = toDayStart(date);
    const key = [
      section,
      targetId || "none",
      projectId || "none",
      toDayKey(day),
      label,
    ].join("|");
    const existing = reminderMap.get(key);
    if (existing) {
      reminderMap.set(key, {
        ...existing,
        count: Number(existing.count || 1) + 1,
        amount: Number(existing.amount || 0) + amount,
      });
      return;
    }
    reminderMap.set(key, {
      id: key,
      section,
      label,
      date: day,
      targetId,
      projectId,
      count: 1,
      amount,
    });
  };

  for (const project of projects || []) {
    const projectId = Number(project?.id);
    const projectName = String(project?.name || `Projet #${projectId || "?"}`);
    const targetId = projectId
      ? `financeur-project-block-${projectId}`
      : undefined;

    for (const funding of project?.realFundings || []) {
      contractsCount += 1;
      if (readFundingIsVerdeo(funding)) {
        contractsVerdeoCount += 1;
      } else {
        contractsInternalCount += 1;
      }

      const contractDate = toValidDate(funding?.dateAmount);
      if (contractDate) {
        calendarEvents.push({
          date: contractDate,
          label: readFundingIsVerdeo(funding)
            ? `${projectName} · Contrat via Verdeo`
            : `${projectName} · Contrat interne`,
          targetId,
          projectId,
        });
      }

      const expectedPaids = normalizeCollectionLike(funding?.realFundingExpectedPaids);
      for (const paid of expectedPaids) {
        const plannedAt = toValidDate(paid?.at);
        const amount = Math.max(0, Number(paid?.amount ?? 0) || 0);

        if (plannedAt) {
          calendarEvents.push({
            date: plannedAt,
            label: `${projectName} · Versement prévu`,
            targetId,
            projectId,
            amount,
          });
        }

        if (isExpectedPaidAlreadyReceived(paid)) continue;
        if (amount <= 0 || !plannedAt) continue;

        plannedOutstandingCount += 1;
        plannedOutstandingAmount += amount;

        const plannedDay = toDayStart(plannedAt);
        const plannedTs = plannedDay.getTime();
        if (plannedTs < today.getTime()) {
          overdueCount += 1;
          overdueAmount += amount;
          pushReminder(
            "overdue",
            `${projectName} · Versement prévu à effectuer`,
            plannedDay,
            targetId,
            Number.isFinite(projectId) ? projectId : undefined,
            amount,
          );
        } else if (plannedTs === today.getTime()) {
          todayCount += 1;
          todayAmount += amount;
          pushReminder(
            "today",
            `${projectName} · Versement prévu aujourd'hui`,
            plannedDay,
            targetId,
            Number.isFinite(projectId) ? projectId : undefined,
            amount,
          );
        } else if (plannedTs <= upcomingLimit.getTime()) {
          upcomingCount += 1;
          upcomingAmount += amount;
          pushReminder(
            "upcoming",
            `${projectName} · Versement prévu`,
            plannedDay,
            targetId,
            Number.isFinite(projectId) ? projectId : undefined,
            amount,
          );
        }
      }

      const realPaids = normalizeCollectionLike(funding?.realFundingPaids);
      if (realPaids.length > 0) {
        for (const paid of realPaids) {
          const amount = Math.max(0, Number(paid?.amount ?? 0) || 0);
          if (amount > 0) {
            receivedCount += 1;
            receivedAmount += amount;
          }
          const paidAt = toValidDate(paid?.at);
          if (paidAt) {
            calendarEvents.push({
              date: paidAt,
              label: `${projectName} · Versement reçu`,
              targetId,
              projectId,
              amount,
            });
          }
        }
      } else {
        for (const paid of expectedPaids) {
          if (!isExpectedPaidAlreadyReceived(paid)) continue;
          const amount = Math.max(0, Number(paid?.amount ?? 0) || 0);
          if (amount > 0) {
            receivedCount += 1;
            receivedAmount += amount;
          }
          const paidDate = toValidDate(paid?.datePaid ?? paid?.at);
          if (paidDate) {
            calendarEvents.push({
              date: paidDate,
              label: `${projectName} · Versement reçu`,
              targetId,
              projectId,
              amount,
            });
          }
        }
      }
    }
  }

  const weekCount = todayCount + upcomingCount;
  const weekAmount = todayAmount + upcomingAmount;

  const stats: ContributionTrackingStat[] = [
    {
      key: "contracts",
      label: "Contrats signés",
      value: contractsCount,
      suffix: "contrats",
      helper: `${contractsVerdeoCount} via Verdeo · ${contractsInternalCount} internes`,
      tone: "olive",
    },
    {
      key: "planned",
      label: "Versements à effectuer",
      value: plannedOutstandingCount,
      suffix: "versements",
      helper: `${format2(plannedOutstandingAmount)} €`,
      tone: "teal",
    },
    {
      key: "overdue",
      label: "Versements en retard",
      value: overdueCount,
      suffix: "versements",
      helper: `${format2(overdueAmount)} €`,
      tone: "rose",
    },
    {
      key: "week",
      label: "À venir (7 jours)",
      value: weekCount,
      suffix: "versements",
      helper: `${format2(weekAmount)} €`,
      tone: "blue",
    },
    {
      key: "received",
      label: "Versements reçus",
      value: receivedCount,
      suffix: "versements",
      helper: `${format2(receivedAmount)} €`,
      tone: "sand",
    },
  ];

  const sectionOrder: Record<ContributionReminderSection, number> = {
    overdue: 0,
    today: 1,
    upcoming: 2,
  };
  const reminderItems = Array.from(reminderMap.values()).sort((a, b) => {
    const sectionDelta = sectionOrder[a.section] - sectionOrder[b.section];
    if (sectionDelta !== 0) return sectionDelta;
    const da = toValidDate(a.date)?.getTime() ?? 0;
    const db = toValidDate(b.date)?.getTime() ?? 0;
    return da - db;
  });

  return { stats, calendarEvents, reminderItems };
}

/** ===================== Racine ===================== */
export default function NewContributions({
  id,
  year,
}: {
  id?: number;
  year: number;
}) {
  const { user } = useAuth();
  const connectedFunderId = getConnectedFunderId(user);
  const [projects, setProjects] = useState<Project[]>([]);

  const { loading, data } = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project`,
    "financeurs",
    1000 * 60 * 60
  );

  const allProjects: Project[] = useMemo(() => {
    const raw = (data as APIResp)?.data;
    const list = Array.isArray(raw) ? raw : [];
    return formatData(list, id);
  }, [data, id]);

  // Filtrer les financements pour ne garder que ceux du financeur connecté
  const initialProjects = useMemo<Project[]>(() => {
    if (!connectedFunderId) return [];
    return allProjects
      .map((p) => ({
        ...p,
        realFundings: (p.realFundings || []).filter(
          (rf) => Number(rf?.financier?.id) === Number(connectedFunderId)
        ),
      }))
      .filter((p) => (p.realFundings?.length ?? 0) > 0);
  }, [allProjects, connectedFunderId]);

  useEffect(() => {
    setProjects(initialProjects);
  }, [initialProjects]);

  const trackingModel = useMemo(
    () => buildContributionTrackingModel(projects),
    [projects],
  );

  const handleCalendarEventClick = (item: ContributionCalendarDetailItem) => {
    const targetId = String(item?.targetId || "");
    if (!targetId) return;
    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.requestAnimationFrame(scrollToTarget);
    window.setTimeout(scrollToTarget, 160);
    window.setTimeout(scrollToTarget, 500);
  };

  const handleReminderClick = (item: ContributionReminderItem) => {
    handleCalendarEventClick({
      label: item.label,
      count: Number(item.count ?? 1),
      targetId: item.targetId,
      projectId: item.projectId,
      amount: item.amount,
    });
  };

  if (loading || !connectedFunderId) {
    return <NewContributionsSkeleton />;
  }

  if (!projects.length) {
    return (
      <div className="text-sm text-gray-600">
        Aucune contribution trouvée pour votre compte.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ContributionTrackingIntroPanel
        title="Suivi de contributions"
        subtitle="Retrouvez les contrats, les échéances de versement et les paiements reçus."
        stats={trackingModel.stats}
        calendarEvents={trackingModel.calendarEvents}
        reminderItems={trackingModel.reminderItems}
        onCalendarEventClick={handleCalendarEventClick}
        onReminderClick={handleReminderClick}
      />

      <ProgramProjectFilter
        projects={initialProjects}
        onFilteredChange={(filtered) => {
          // ici tu peux setState(filtered) si tu veux remplacer ton state projects
          setProjects(filtered);
        }}
      />
      {projects.map((project) => (
        <div
          key={project.id}
          id={`financeur-project-block-${Number(project.id)}`}
        >
          <ProjectBlockView id={id} project={project} year={year} />
        </div>
      ))}
    </div>
  );
}

/** ===================== Bloc Projet (lecture seule) ===================== */

function ProjectBlockView({
  id,
  project,
  year,
}: {
  id?: number;
  project: Project;
  year: number;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [currencySelected, setCurrencySelected] = useState<string>("EUR");

  const projectCurrency = project?.country?.currency;
  const projectCurrencyCode = projectCurrency?.code;
  const projectCurrencySymbol = projectCurrency?.symbol || "";

  const isProjectEuro =
    (projectCurrencyCode ?? "").trim().toUpperCase() === "EUR";

  // Listes converties pour l’affichage
  const [fundingsDisplay, setFundingsDisplay] = useState<any[]>(
    project.realFundings || []
  );

  // conversion complète (contrat + prévus + réalisés) quand devise change
  useEffect(() => {
    (async () => {
      const next = await buildConvertedFundings(
        project.realFundings || [],
        currencySelected,
        projectCurrency
      );
      setFundingsDisplay(next);
    })();
  }, [project.realFundings, currencySelected, projectCurrency?.id]);

  const onCurrencyChange = async (val: string) => setCurrencySelected(val);

  const totalContractsDisplayed = useMemo(
    () =>
      (fundingsDisplay || []).reduce(
        (sum, f) => sum + (Number(f?.paidAmount ?? 0) || 0),
        0
      ),
    [fundingsDisplay]
  );

  const currencyDisplayLabel =
    currencySelected === "EUR"
      ? "€"
      : projectCurrencySymbol || projectCurrencyCode || currencySelected;

  return (
    <Card className="rounded-2xl border bg-white shadow-sm">
      <CardHeader className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {id === null ? (
              <a
                href={`/dashboard/project/${project.id}`}
                className="text-[#1f2937] text-sm font-semibold hover:underline inline-flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={24} className="w-4 h-4 text-[#434653]" />
                <span className="truncate">{project.name}</span>
              </a>
            ) : (
              <div className="text-[#1f2937] text-sm font-semibold truncate">
                {project.name}
              </div>
            )}

            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[#6b7280]">
              <span>{fundingsDisplay.length} contrat(s)</span>
              <span>•</span>
              <span className="inline-flex items-center gap-1">
                Total contrats : {format2(totalContractsDisplayed)}{" "}
                {currencyDisplayLabel}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center"
                      aria-label="Information sur le total des contrats"
                    >
                      <Info className="w-3.5 h-3.5 text-[#94a3b8]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="z-50 w-[280px] text-xs text-slate-700">
                    Le total des contrats correspond à la somme des contrats
                    affichés dans la devise sélectionnée pour ce projet.
                  </PopoverContent>
                </Popover>
              </span>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            <Select value={currencySelected} onValueChange={onCurrencyChange}>
              <SelectTrigger className="h-8 w-[120px]">
                <SelectValue placeholder="Devise" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EUR">€</SelectItem>
                {projectCurrencyCode && !isProjectEuro && (
                  <SelectItem value={projectCurrencyCode}>
                    {projectCurrencySymbol || projectCurrencyCode}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            <button
              type="button"
              className="p-1 rounded hover:bg-slate-100"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Réduire" : "Développer"}
            >
              {isOpen ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {isOpen && (
        <CardContent className="px-4 pb-4 pt-0">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              Vue globale
            </div>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <MonthlyFinanceMatrix year={year} fundingsDisplay={fundingsDisplay} />

          <div className="mt-5 flex items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              Contrats et versements
            </div>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          {fundingsDisplay.length > 0 ? (
            <div className="mt-3 space-y-3">
              {fundingsDisplay.map((f, idx) => {
                const base = (project.realFundings || []).find(
                  (x) => Number(x.id) === Number(f.id)
                );
                return (
                  <FundingReadonlyRow
                    key={f.id ?? idx}
                    funding={f}
                    fundingBase={base || f}
                    currencySelected={currencySelected}
                    projectCurrencySymbol={projectCurrencySymbol}
                    projectCurrency={projectCurrency}
                  />
                );
              })}
            </div>
          ) : (
            <div className="mt-3 rounded-xl border bg-white p-4 text-sm text-gray-500">
              Aucune contribution.
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/** ===================== Ligne financement (lecture seule + VersementCard en popover) ===================== */

function FundingReadonlyRow({
  funding,
  fundingBase,
  currencySelected,
  projectCurrencySymbol,
  projectCurrency,
}: {
  funding: any; // version convertie pour l’affichage de la ligne
  fundingBase: any; // version "brute" pour VersementCard (on y lit realFundingExpectedPaids)
  currencySelected: string;
  projectCurrencySymbol: string;
  projectCurrency?: { id: number; code: string; symbol?: string };
}) {
  const symbol = currencySelected === "EUR" ? "€" : projectCurrencySymbol;

  // ✅ On passe *uniquement* realFundingExpectedPaids à VersementCard
  const fundingForCard = useMemo(() => {
    return {
      ...fundingBase,
      realFundingExpectedPaids: Array.isArray(
        fundingBase?.realFundingExpectedPaids
      )
        ? fundingBase.realFundingExpectedPaids
        : [],
    };
  }, [fundingBase]);

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="text-xs font-medium text-slate-500">
            Montant du contrat
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900">
            {typeof funding.paidAmount === "number"
              ? `${format2(funding.paidAmount)} ${symbol}`
              : "-"}
          </div>

          <div className="mt-2 text-xs text-slate-600">
            Date de signature :{" "}
            <span className="font-medium text-slate-700">
              {funding.dateAmount
                ? new Date(funding.dateAmount).toLocaleDateString()
                : "-"}
            </span>
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          {fundingForCard.realFundingExpectedPaids.length > 0 ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <ChevronDown className="w-4 h-4" />
                  Voir les versements
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="end"
                className="z-50 p-0 w-[760px] max-w-[90vw]"
              >
                <div className="max-h-[70vh] overflow-auto p-4 bg-white rounded-md shadow-lg">
                  <VersementCard
                    currency={projectCurrency}
                    currencySelected={
                      currencySelected === "EUR"
                        ? null
                        : (currencySelected as any)
                    }
                    funding={fundingForCard}
                    desiredFundings={[]}
                    onChange={() => {}}
                    readonly={true}
                  />
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="text-xs text-slate-500">Aucun versement</div>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-slate-50/80 p-3">
        <div className="text-xs font-medium text-slate-500 mb-2">
          Pièces jointes du contrat
        </div>
        {funding.paidPjs?.length ? (
          <div className="flex flex-col gap-1">
            {funding.paidPjs.map((pj: any) => (
              <a
                target="_blank"
                href={import.meta.env.VITE_MEDIA_BASE + pj.uri}
                key={pj.id}
                className="text-sm text-slate-700 hover:underline truncate"
                rel="noreferrer"
              >
                {pj.name}
              </a>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500">Aucune pièce jointe.</div>
        )}
      </div>
    </div>
  );
}

/** ===================== Matrice simplifiée (contrats / prévu / versé) ===================== */

function MonthlyFinanceMatrix({
  year,
  fundingsDisplay,
}: {
  year: number;
  fundingsDisplay: any[];
}) {
  // Agrégats mensuels
  const rec = Array(12).fill(0) as number[]; // Contrat signé
  const prev = Array(12).fill(0) as number[]; // Versement prévu
  const real = Array(12).fill(0) as number[]; // Versement réalisé

  for (const f of fundingsDisplay || []) {
    // Contrat signé
    const ca = Number(f?.paidAmount ?? 0) || 0;
    const d = f?.dateAmount ? new Date(f.dateAmount) : null;
    if (ca && d && !isNaN(d.getTime()) && d.getFullYear() === year) {
      rec[d.getMonth()] += ca;
    }

    // Versements prévus
    for (const p of f.realFundingExpectedPaidsConverted || []) {
      const a = Number(p?.amount ?? 0) || 0;
      const dp = p?.at ? new Date(p.at) : null;
      if (a && dp && !isNaN(dp.getTime()) && dp.getFullYear() === year) {
        prev[dp.getMonth()] += a;
      }
    }

    // Versements réalisés
    // Priorité aux versements réellement saisis (realFundingPaids), déjà convertis avec amountConverted si dispo.
    if (Array.isArray(f.realFundingPaidsConverted) && f.realFundingPaidsConverted.length) {
      for (const p of f.realFundingPaidsConverted) {
        const a = Number(p?.amount ?? 0) || 0;
        const dp = p?.at ? new Date(p.at) : null;
        if (a && dp && !isNaN(dp.getTime()) && dp.getFullYear() === year) {
          real[dp.getMonth()] += a;
        }
      }
    } else {
      for (const p of f.realFundingExpectedPaidsConverted || []) {
        if (p?.status !== 2) continue;
        const a = Number(p?.amount ?? 0) || 0;
        const dp = p?.datePaid ? new Date(p.datePaid) : null;
        if (a && dp && !isNaN(dp.getTime()) && dp.getFullYear() === year) {
          real[dp.getMonth()] += a;
        }
      }
    }
  }

  return (
    <div className="mt-3 space-y-4">
      <div className="rounded-3xl bg-white px-4 py-4 ring-1 ring-slate-200">
        <MatrixLine
          label="Contrats signés"
          values={rec}
          year={year}
          rowKey="rec"
          type={4}
        />
      </div>

      <div className="rounded-3xl bg-[#F3FAF3] px-4 py-4">
        <MatrixLine
          label="Versements prévus"
          values={prev}
          year={year}
          rowKey="prev"
          type={5}
          className="mb-3"
        />
        <MatrixLine
          label="Versements reçus"
          values={real}
          year={year}
          rowKey="real"
          type={6}
        />
      </div>
    </div>
  );
}

function MatrixLine({
  label,
  values,
  year,
  rowKey,
  type,
  className = "",
}: {
  label: string;
  values: number[];
  year: number;
  rowKey: string;
  type: number;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-3 w-full ${className}`}>
      <div className="w-1/6 text-sm font-medium text-[#586071]">{label}</div>
      <div className="flex w-5/6 flex-wrap gap-2">
        {values.map((v, i) => (
          <FieldInput
            key={`matrix-${rowKey}-${year}-${i}`}
            type={type}
            defaultValue={v || 0}
            valueMultiple={v || 0}
            data={{}}
          />
        ))}
      </div>
    </div>
  );
}

/** ===================== Helpers ===================== */

const historicalRateToEuroCache = new Map<string, Promise<number | null>>();

function isNonVerdeoFundingContract(funding: any): boolean {
  if (!funding) return false;
  if (funding.verdeo === false) return true;
  if (funding.verdeo === true) return false;
  if (funding.verdeoFundingInvoiced != null || funding.verdeoFundingPaid != null) {
    return false;
  }
  return true;
}

function normalizeDateOnly(input: unknown): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw;
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

async function fetchHexaRateToEuroAtDate(
  currencyCode: string,
  dateInput: unknown
): Promise<number | null> {
  const code = String(currencyCode ?? "")
    .trim()
    .toUpperCase();
  const day = normalizeDateOnly(dateInput);
  if (!code || !day) return null;
  if (code === "EUR") return 1;

  const cacheKey = `${code}@${day}`;
  const cached = historicalRateToEuroCache.get(cacheKey);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const response = await fetch(
        `https://api.frankfurter.dev/v2/rates?base=EUR&quotes=${code}&date=${day}`
      );
      if (!response.ok) return null;
      const payload = await response.json();
      const entries = Array.isArray(payload) ? payload : [];
      const entry = (entries as any[]).find((e: any) => e.quote === code);
      const rate = Number(entry?.rate);
      if (!Number.isFinite(rate) || rate <= 0) return null;
      const rateToEuro = 1 / rate;
      return Number.isFinite(rateToEuro) && rateToEuro > 0 ? rateToEuro : null;
    } catch {
      return null;
    }
  })();

  historicalRateToEuroCache.set(cacheKey, promise);
  return promise;
}

function normalizeCollectionLike(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const values = Object.values(value);
  if (!values.length) return [];

  const looksLikeEntityCollection = values.every(
    (item) => item && typeof item === "object"
  );
  return looksLikeEntityCollection ? (values as any[]) : [];
}

function readCodeFromCurrencyLike(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return null;
    const code = raw.toUpperCase();
    return code || null;
  }
  if (typeof value === "object") {
    const raw = String(value?.code ?? value?.currencyCode ?? "").trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return null;
    const code = raw.toUpperCase();
    return code || null;
  }
  return null;
}

function readIdFromCurrencyLike(value: any): number | null {
  const raw =
    typeof value === "object"
      ? value?.id
      : typeof value === "number" || typeof value === "string"
      ? value
      : null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readFundingInputCurrencyCode(
  funding: any,
  projectCurrency?: Currency
): string | null {
  const obtainedRows = normalizeCollectionLike(
    funding?.obtainedFundings ??
      funding?.obtainedFunding ??
      funding?.obtained_fundings ??
      funding?.obtained_funding
  );

  for (const row of obtainedRows) {
    const code =
      readCodeFromCurrencyLike(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount
      ) ??
      (Number(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount
      ) === 3
        ? "EUR"
        : null);
    if (code) return code;
  }

  const directCode =
    readCodeFromCurrencyLike(funding?.paidDevise) ??
    readCodeFromCurrencyLike(funding?.paid_devise) ??
    readCodeFromCurrencyLike(funding?.devise);
  if (directCode) return directCode;

  const directId =
    readIdFromCurrencyLike(funding?.paidDevise) ??
    readIdFromCurrencyLike(funding?.paid_devise) ??
    readIdFromCurrencyLike(funding?.devise);
  if (directId === 3) return "EUR";

  const projectCurrencyId = readIdFromCurrencyLike(projectCurrency);
  const projectCurrencyCode = readCodeFromCurrencyLike(projectCurrency);
  if (
    directId &&
    projectCurrencyId &&
    directId === projectCurrencyId &&
    projectCurrencyCode
  ) {
    return projectCurrencyCode;
  }

  return null;
}

function isFundingInputInLocalCurrency(
  funding: any,
  projectCurrency?: Currency
): boolean {
  const code = readFundingInputCurrencyCode(funding, projectCurrency);
  return !!code && code !== "EUR";
}

/** Convertit toute la structure (contrat + prévus + réalisés) pour affichage
 *  - Non-Verdeo + devise locale: EUR basé sur paidAmountConverted/amountConverted via taux contrat
 *  - Non-Verdeo + devise EUR: EUR = paidAmount stocké, conversion locale via taux contrat
 *  - Verdeo: EUR stocké côté API, puis conversion visuelle si devise projet sélectionnée
 */
async function buildConvertedFundings(
  baseFundings: any[],
  currencySelected: string,
  projectCurrency?: Currency
) {
  const pc = projectCurrency;
  const needConv = currencySelected !== "EUR" && !!pc?.code;

  const readNumber = (value: any): number | null => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  return Promise.all(
    (baseFundings || []).map(async (f) => {
      const isNonVerdeoContract = isNonVerdeoFundingContract(f);
      const fundingInputIsLocalCurrency = isFundingInputInLocalCurrency(f, pc);
      const fundingStoredEuro = readNumber(f?.paidAmount);
      const fundingConvertedStored =
        isNonVerdeoContract && fundingInputIsLocalCurrency
          ? readNumber(f?.paidAmountConverted) ??
            readNumber(f?.paid_amount_converted) ??
            null
          : null;
      const inferredStoredRateToEuro =
        fundingStoredEuro != null &&
        fundingStoredEuro > 0 &&
        fundingConvertedStored != null &&
        fundingConvertedStored > 0
          ? fundingStoredEuro / fundingConvertedStored
          : null;
      const fundingHistoricalRateToEuro = isNonVerdeoContract
        ? await fetchHexaRateToEuroAtDate(pc?.code ?? "", f?.dateAmount)
        : null;
      const fundingRateToEuro =
        (fundingHistoricalRateToEuro && fundingHistoricalRateToEuro > 0
          ? fundingHistoricalRateToEuro
          : null) ??
        (inferredStoredRateToEuro && inferredStoredRateToEuro > 0
          ? inferredStoredRateToEuro
          : null);

      // Montant contrat
      let paidAmountEuro = Number(fundingStoredEuro ?? 0);
      if (
        isNonVerdeoContract &&
        fundingInputIsLocalCurrency &&
        fundingConvertedStored != null &&
        fundingRateToEuro != null &&
        fundingRateToEuro > 0
      ) {
        paidAmountEuro = fundingConvertedStored * fundingRateToEuro;
      }

      let paidAmountLocal: number | null = isNonVerdeoContract
        ? fundingInputIsLocalCurrency
          ? fundingConvertedStored
          : null
        : null;
      if (
        isNonVerdeoContract &&
        (paidAmountLocal == null || paidAmountLocal <= 0) &&
        paidAmountEuro > 0 &&
        fundingRateToEuro &&
        fundingRateToEuro > 0
      ) {
        paidAmountLocal = paidAmountEuro / fundingRateToEuro;
      } else if (
        (paidAmountLocal == null || paidAmountLocal <= 0) &&
        paidAmountEuro > 0 &&
        needConv
      ) {
        paidAmountLocal = Number(await convertEuroToCurrency(paidAmountEuro, pc));
      }

      const paidAmount =
        currencySelected === "EUR"
          ? paidAmountEuro
          : needConv
          ? Number(paidAmountLocal ?? paidAmountEuro ?? 0)
          : paidAmountEuro;

      // Versements réalisés
      const fundingPaids = f.realFundingPaids;
      const realFundingPaidsConverted = Array.isArray(fundingPaids)
        ? await Promise.all(
            fundingPaids.map(async (p: RealFundingPaid & any) => {
              const pRateToEuro =
                isNonVerdeoContract
                  ? fundingRateToEuro
                  : null;
              const pAmountConvertedStored =
                isNonVerdeoContract && fundingInputIsLocalCurrency
                  ? readNumber(p?.amountConverted) ??
                    readNumber(p?.amount_converted) ??
                    null
                  : null;
              const pAmountEuro = Number(p?.amount || 0);

              let displayAmount = pAmountEuro;
              if (currencySelected === "EUR") {
                if (
                  isNonVerdeoContract &&
                  fundingInputIsLocalCurrency &&
                  pAmountConvertedStored != null &&
                  pRateToEuro != null &&
                  pRateToEuro > 0
                ) {
                  displayAmount = pAmountConvertedStored * pRateToEuro;
                }
              } else if (needConv) {
                if (
                  isNonVerdeoContract &&
                  fundingInputIsLocalCurrency &&
                  pAmountConvertedStored != null
                ) {
                  displayAmount = pAmountConvertedStored;
                } else if (
                  isNonVerdeoContract &&
                  pRateToEuro &&
                  pRateToEuro > 0
                ) {
                  displayAmount = pAmountEuro / pRateToEuro;
                } else {
                  displayAmount = Number(
                    await convertEuroToCurrency(pAmountEuro || 0, pc)
                  );
                }
              }

              return {
                ...p,
                amount: displayAmount,
                amountConverted: pAmountConvertedStored,
                oldRate: pRateToEuro,
                dateOldRate: isNonVerdeoContract
                  ? normalizeDateOnly(f?.dateAmount)
                  : p.dateOldRate,
                pj: p.pjs?.length ? p.pjs[0] : null,
              } as any;
            })
          )
        : [];

      // Versements prévus
      const expectedPaids = f.realFundingExpectedPaids;
      const realFundingExpectedPaidsConverted = Array.isArray(expectedPaids)
        ? await Promise.all(
            expectedPaids.map(async (p: RealFundingExpectedPaid & any) => {
              const pRateToEuro =
                isNonVerdeoContract
                  ? fundingRateToEuro
                  : null;
              const pAmountConvertedStored =
                isNonVerdeoContract && fundingInputIsLocalCurrency
                  ? readNumber(p?.amountConverted) ??
                    readNumber(p?.amount_converted) ??
                    null
                  : null;
              const pAmountEuro = Number(p.amount || 0);

              let displayAmount = pAmountEuro;
              if (currencySelected === "EUR") {
                if (
                  isNonVerdeoContract &&
                  fundingInputIsLocalCurrency &&
                  pAmountConvertedStored != null &&
                  pRateToEuro != null &&
                  pRateToEuro > 0
                ) {
                  displayAmount = pAmountConvertedStored * pRateToEuro;
                }
              } else if (needConv) {
                if (
                  isNonVerdeoContract &&
                  fundingInputIsLocalCurrency &&
                  pAmountConvertedStored != null
                ) {
                  displayAmount = pAmountConvertedStored;
                } else if (
                  isNonVerdeoContract &&
                  pRateToEuro &&
                  pRateToEuro > 0
                ) {
                  displayAmount = pAmountEuro / pRateToEuro;
                } else {
                  displayAmount = Number(
                    await convertEuroToCurrency(pAmountEuro || 0, pc)
                  );
                }
              }

              return {
                ...p,
                amount: displayAmount,
              };
            })
          )
        : [];

      // PJ contrat
      const paidPjs = f?.paidPjs || [];

      return {
        ...f,
        paidAmount,
        realFundingPaidsConverted,
        realFundingExpectedPaidsConverted,
        paidPjs,
      };
    })
  );
}

/** Nettoyage léger comme dans ton formatData original (Verdeo) */
function formatData(data: any[], id?: number): Project[] {
  return (data || [])
    .filter((d: any) => id === null || id === undefined || d.id === id)
    .map((d: any) => ({
      ...d,
      realFundings: (d.realFundings || []).map((rf: any) => ({
        ...rf,
        representant: rf.verdeo
          ? { firstname: "Verdeo", lastname: "" }
          : rf.representant,
      })),
    }));
}

function getConnectedFunderId(user: any): number | undefined {
  const cand = user?.id;
  return cand != null ? Number(cand) : undefined;
}

const MONTHS = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Juin",
  "Juil",
  "Août",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

function format2(n: number): string {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
